<script>
import { nextTick } from 'vue';
import { deleteOnePost, likeOnePost, createOnePostComment, deleteOnePostComment } from '@/services/MindMovieServices';
import ImageCacheService from '@/services/ImageCacheService';

export default {
  props: [ 'post'],
  emits: [],
  data() {
    return {
      uId: localStorage.getItem('uId'),
      token: localStorage.getItem('idToken'),

      // actions
      postLiked: false,
      showCommentsSection: false,
      editThePost: false, // show modal to edit the post

      recentlyAddedComments: {},
      newComment: '',

      // "custom" image cache
      cachedImageUrls: {}, // url -> objectURL
      loadingImages: new Set(),

    };
  },
  computed: {
    likeCount() {
      let c = this.post.likes;
      if (this.postLiked) {
        c = c + 1;
      }
      return c;
    },
    allComments() {
      let ac = {
        ...this.post.comments,
        ...this.recentlyAddedComments
      };

      let arrayAC = [];
      for (let id of Object.keys(ac)) {
        arrayAC.push({
          id,
          ...ac[id],
        });
      }
      // return [...arrayAC].sort((a, b) => {
      return arrayAC.sort((a, b) => {
        return a.date - b.date; // Sort by date (number)
      });

    },

    customCarouselItems() {
      let c = null;
      if (this.post.type === 'custom') {
        // c =  JSON.parse(this.post.src);
        c = this.post.src;
      }
      return c;
    },
    processedCustomItems() {
      if (this.post.type !== 'custom') return [];
      return this.customCarouselItems.map(url => this.cachedImageUrls[url] || url);
    },
  },
  methods: {
    // HELPERS
    async loadImgurScript() {
      const s = document.createElement('script');
      s.src = `https://s.imgur.com/min/embed.js?cb=${Date.now()}`;
      s.async = true;
      await nextTick();
      document.body.appendChild(s);
    },
    async ensureImageCached(url) {
      // console.log('🔄 ensureImageCached called for:', url);

      if (this.cachedImageUrls[url] || this.loadingImages.has(url)) {
        // console.log('⏭️ Already cached or loading:', url);
        return;
      }

      this.loadingImages.add(url);

      try {
        // console.log('📡 Checking cache for:', url);
        let blob = await ImageCacheService.getImage(url);

        if (!blob) {
          // console.log('🌐 Cache miss - fetching from network:', url);
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          // console.log('*** fetch response ***', response);

          blob = await response.blob();
          // console.log('✅ Fetched blob, saving to IndexedDB:', url);

          const saved = await ImageCacheService.saveImage(url, blob);
          console.log('💾 saveImage result:', saved);
        } else {
          // console.log('✅ Found in cache:', url);
        }

        const objectUrl = URL.createObjectURL(blob);
        this.cachedImageUrls[url] = objectUrl;
        // console.log('🖼️ Created object URL for:', url);

      } catch (err) {
        // console.error('❌ Image cache failed for:', url, err);
        this.cachedImageUrls[url] = url; // fallback
      } finally {
        this.loadingImages.delete(url);
      }
    },

    // Call this when component mounts or when custom post is visible
    async preloadCustomImages() {
      if (this.post.type !== 'custom') return;

      // console.log('🚀 preloadCustomImages for post:', this.post.id);
      // console.log('Images to cache:', this.customCarouselItems);

      for (const url of this.customCarouselItems) {
        await this.ensureImageCached(url);   // make it sequential for better logs
      }
    },

    // POST ACTION BAR
    async doubleTapThisPost() {
      let newLikes = this.post.likes;
      newLikes = this.postLiked ? newLikes - 1 : newLikes + 1;
      // API call
      const postWasUpdated = await likeOnePost(this.uId, this.token, this.post.id, newLikes);
      if (postWasUpdated) { // update the UI
        this.postLiked = !this.postLiked;
      }
    },
    async leaveAComment() {
      // console.log('*** SinglePost >> leaveAComment() >> post.id, newComment ***', this.post.id, this.newComment);
      const today = new Date();
      const commentDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
      let comment = {
        comment: this.newComment.trim(),
        date: commentDate
      };
      // console.log('*** SinglePost >> leaveAComment() >> finna leave a new comment on post.id ***', this.post.id, comment);
      const commentWasPosted = await createOnePostComment(this.uId, this.token, this.post.id, comment);
      if (commentWasPosted) {
        const newId = commentWasPosted.name;
        this.recentlyAddedComments[newId] = comment;
        this.newComment = '';
      }
    },
    sharePost() {
      let shareUrl = '';

      // format depending on post type
      if (this.post.type === 'youtube') {
        shareUrl = `https://www.youtube.com/watch?v=${this.post.src}`;
      } else if (this.post.type === "imgur") {
        shareUrl = `https://imgur.com/${this.post.src}`; // imgur direct link
      } else if (this.post.type === 'image') {
        shareUrl = `https://google.com`;
      }

      if (navigator.share) {
        navigator.share({ url: shareUrl });
      } else {
        // create SMS link
        const messageBody = encodeURIComponent(`Check this out: ${shareUrl}`);
        // const smsLink = `sms:?body=${messageBody}`;
        // window.location.href = smsLink;
        window.location.href = `sms:?body=${messageBody}`;
      }
    },

    async deleteOneComment(id) {
      // console.log('*** SinglePost >> deleteOneComment() >> post.id, commentId ***', this.post.id, id);
      await deleteOnePostComment(this.uId, this.token, this.post.id, id);

      delete this.post.comments[id];
    },
    async deletePost() {
      await deleteOnePost(this.uId, this.token, this.post.id);
      const p = document.getElementById(`single-post-${this.post.id}`);
      if (p) {
        p.classList.add('d-none');
      }
      this.editThePost = false;
    },
  },
  mounted() {
    if (this.post.type === 'imgur') {
      this.loadImgurScript();
    }

    // if (this.post.type === 'custom') {
    //   this.preloadCustomImages();
    // }

  },

}
</script>

<template>
  <div :id="`single-post-${post.id}`" class="mb-16">

    <!-- VIEW THE POST -->
    <div v-if="!editThePost" class="single-post-view-container">

      <!-- YOUTUBE -->
      <div v-if="post.type === 'youtube'">
        <iframe
        :src="`https://www.youtube.com/embed/${post.src}?autoplay=0&loop=1&playlist=${post.src}&mute=1&cc_load_policy=1`"
        class="iframe-youtube"
        frameborder="0"
        allowfullscreen
        ></iframe>
        <div class="post-spacer-for-actions"></div>
      </div>

      <!-- IMGUR -->
      <div v-if="post.type === 'imgur'">
        <blockquote
        class="imgur-embed-pub"
        lang="en"
        :data-id="`${post.src}`"
        data-context="false"
        >
          <a :href="`https://imgur.com/${post.src}`"></a>
        </blockquote>
      </div>

      <!-- CUSTOM -->
      <div v-if="post.type === 'custom'" class="custom-carousel-wrapper">
        <v-carousel
          :show-arrows="customCarouselItems.length > 1"
          :progress="customCarouselItems.length > 1 ? 'grey-darken-2' : 'transparent'"
          hide-delimiters
          :continuous="true"
          :cycle="false"
        >
          <v-carousel-item v-for="(url, i) of processedCustomItems" :key="i">
            <v-img :src="url" contain></v-img>
          </v-carousel-item>

          <!-- Custom Prev Arrow -->
          <template v-slot:prev="{ props }">
            <v-btn
              v-if="customCarouselItems.length > 1"
              icon
              class="custom-carousel-arrow left"
              @click="props.onClick"
              :disabled="props.disabled"
            >
              <v-icon size="36">mdi-chevron-left</v-icon>
            </v-btn>
          </template>

          <!-- Custom Next Arrow -->
          <template v-slot:next="{ props }">
            <v-btn
              v-if="customCarouselItems.length > 1"
              icon
              class="custom-carousel-arrow right"
              @click="props.onClick"
              :disabled="props.disabled"
            >
              <v-icon size="36">mdi-chevron-right</v-icon>
            </v-btn>
          </template>

          <!-- same for next -->
        </v-carousel>
      </div>



      <!-- POST ACTIONS BAR -->
      <div class="post-actions d-flex align-end justify-space-between my-3">

        <!-- LIKES-->
        <div class="d-flex justify-start align-center">
          <!-- icon -->
          <div v-if="postLiked" @click="doubleTapThisPost"><v-icon>mdi-heart</v-icon></div>
          <v-icon v-else @click="doubleTapThisPost">mdi-heart-outline</v-icon>
          <!-- count -->
          <p v-if="likeCount" class="ml-2">{{ likeCount }}</p>
        </div>

        <!-- COMMENTS-->
        <div class="d-flex justify-start align-center">
          <v-icon @click="showCommentsSection = !showCommentsSection">mdi-message-reply-outline</v-icon>
          <p v-if="post.comments" class="ml-2">{{Object.keys(post.comments).length}}</p>
        </div>

        <!-- SHARE -->
        <div>
          <v-icon @click="sharePost" style="font-size: 28px;">mdi-share-outline</v-icon>
        </div>

        <!-- EDIT/MENU -->
        <div>
          <v-icon @click="editThePost = true">mdi-menu</v-icon>
        </div>
      </div>


      <!-- COMMENTS SECTION-->
      <div v-if="showCommentsSection" class="single-post-comments-section">

        <!-- add a comment -->
        <div class="d-flex">
          <v-textarea
          v-model="newComment"
          :rules="[v => v.length <= 256 || 'Max 256 characters']"
          counter
          variant="outlined"
          rows="1"
          auto-grow
          label="Leave a comment"
          clearable
          ></v-textarea>
          <v-icon @click="leaveAComment" :color="newComment && newComment.length <= 256 ? 'blue-darken-1' : 'grey'" class="mt-auto mb-9 ml-1">mdi-send</v-icon>
        </div>
        <p @click="showCommentsSection = false" class="text-body-2 mb-3 text-center text-grey-lighten-2 cursor-pointer"><i>- hide comments -</i></p>


        <div v-if="allComments.length > 0" class="px-3 comments-container">
          <!-- the comments -->
          <div v-for="c of allComments" :key="c.id" class="mb-3">
            <p>{{c.comment}}</p>
            <div class="comments-meta d-flex justify-space-between">
              <p class="text-body-2"><i>{{c.date}}</i></p>
              <v-icon @click="deleteOneComment(c.id)" color="red-lighten-1" class="cursor-pointer">mdi-delete-outline</v-icon>
            </div>
          </div>
        </div>

        <p @click="showCommentsSection = false" class="text-body-2 mt-3 text-center text-grey-lighten-2 cursor-pointer"><i>- hide comments -</i></p>
      </div>

    </div>





    <!-- EDIT THE POST -->
    <v-dialog v-model="editThePost" fullscreen persistent>
      <v-card class="py-8 px-4 bg-grey-lighten-4">
        <div id="edit-this-post-container">
          <h3 class="my-3">Edit this Post</h3>

          <p class="my-1">Post type: {{post.type}}</p>

          <div class="my-12">Source(s):
            <a v-if="post.type === 'youtube'" :href="`https://www.youtube.com/watch?v=${post.src}`" target="_blank">{{post.src}}</a>
            <a v-else-if="post.type === 'imgur'" :href="`https://imgur.com/${post.src}`" target="_blank">{{post.src}}</a>
            <ol v-else-if="post.type === 'custom'" class="ms-4">
              <li v-for="(url, i) in customCarouselItems" :key="i">
                <a :href="url" target="_blank">{{url}}</a>
              </li>
            </ol>

          </div>


          <!-- Actions -->
          <div class="edit-post-actions d-flex justify-space-between">
            <v-btn @click.prevent="editThePost = false" color="blue-lighten-1">cancel</v-btn>
            <v-btn @click.prevent="deletePost" color="red-lighten-2">Delete</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>



  </div>
</template>

<style scoped lang="scss">
/* edit the post - modal */
#edit-this-post-container {
  padding-left: 16px;
  padding-right: 16px;
  @media screen and (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    margin: 0 auto;
    width: 600px;
    max-width: 600px;
  }
}

/* view the post */
.single-post-view-container {
  position: relative;
  max-width: 375px;
  max-height: 760px;
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  overflow-x: scroll;

  .post-spacer-for-actions {
    height: 600px;
    width: 100%;
    border:1px solid red;
  }
  .iframe-youtube {
    position: absolute;
    top: 0;
    left: 0;
    height: 600px;
    width: 100%;
  }
  .post-actions > div {
    width: 15%;
    padding-left: 5%;
  }


  .custom-carousel-wrapper {
    position: relative;
    max-width: 375px; /* match your other post styles */
    margin-left: auto;
    margin-right: auto;

    .v-carousel {
      .v-window__container {
        height: 600px; /* adjust to match your youtube spacer / desired image height */
      }
    }

    .custom-carousel-arrow {
      position: absolute;
      bottom: 24px;           /* distance from bottom */
      z-index: 5;
      background-color: rgba(0, 0, 0, 0.4) !important; /* subtle dark bg so they pop */
      backdrop-filter: blur(4px); /* nice modern touch */
      width: 48px;
      height: 48px;
      border-radius: 50%;

      &.left {
        left: 16px;
      }

      &.right {
        right: 16px;
      }

      .v-icon {
        color: white !important;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.6) !important;
      }
    }
  }


}

/* post action bar */
.edit-post-actions {
  .v-btn {
    text-transform: capitalize;
  }
}

/* comments section */
.single-post-comments-section {
  .comments-container {
    max-height: 157px;
    overflow-y: scroll;
    border: 1px solid white;
  }
}
</style>
